import { Pencil, Trash2, Smartphone, Flame, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatPrice, CATEGORIES, categoryOf, conditionLabel, appleUntil } from "@/lib/store";
import { planValues, productPlans, useRates } from "@/lib/pricing";

const AdminPlans = ({ p }) => {
  useRates();
  const plans = productPlans(p);
  if (!plans.length) return null;
  return (
    <div data-testid={`admin-plans-${p.id}`} className="mt-1 space-y-0.5 text-xs text-zinc-300">
      {plans.map((pl) => {
        const v = planValues(p.price, pl);
        return <p key={pl.id}>{pl.n}x de {formatPrice(v.each)} <span className="text-zinc-400">· total parcelado {formatPrice(v.total)}</span></p>;
      })}
    </div>
  );
};

export const AdminProductRow = ({ p, onEdit, onDuplicate, onToggle, onFeature, onDelete }) => {
  const sold = p.status === "vendido";
  return (
    <div data-testid={`admin-product-${p.id}`} className={`flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#111113] p-4 sm:flex-row sm:items-center transition-opacity ${sold ? "opacity-60" : ""}`}>
      <div className="flex flex-1 items-center gap-4 min-w-0">
        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-white/5">
          {p.images?.[0] ? <img src={p.images[0]} alt={p.model} className="h-full w-full object-cover" /> : <Smartphone className="m-auto mt-7 h-5 w-5 text-zinc-500" />}
        </div>
        <div className="min-w-0">
          <p className="font-display truncate text-lg font-bold text-white">{p.model}</p>
          <p className="truncate text-sm text-zinc-400">{[CATEGORIES.find((c) => c.id === categoryOf(p))?.label, p.storage, p.color, conditionLabel(p), p.battery != null ? `Bateria ${p.battery}%` : null, appleUntil(p) ? `Apple até ${appleUntil(p)}` : null].filter(Boolean).join(" · ")}</p>
          <p className="mt-1 text-sm font-semibold text-white">{formatPrice(p.price)} <span className="text-xs font-normal text-zinc-400">à vista</span></p>
          <AdminPlans p={p} />
          {p.featured && p.featured_until && (
            <p data-testid={`admin-featured-until-${p.id}`} className="mt-0.5 text-xs text-amber-300">Oferta até {new Date(p.featured_until).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <Switch data-testid={`admin-toggle-status-${p.id}`} checked={sold} onCheckedChange={() => onToggle(p)} />
          {sold ? "Vendido" : "Disponível"}
        </label>
        <div className="flex gap-2">
          <button data-testid={`admin-feature-${p.id}`} onClick={() => onFeature(p)} title={p.featured ? "Remover da Oferta da semana" : "Tornar Oferta da semana"}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${p.featured ? "border-amber-300 bg-amber-300 text-black" : "border-white/15 text-white hover:bg-white/10"}`}>
            <Flame className="h-4 w-4" /> <span className="hidden sm:inline">{p.featured ? "Oferta da semana" : "Destacar"}</span>
          </button>
          <button data-testid={`admin-duplicate-${p.id}`} onClick={() => onDuplicate(p)} title="Duplicar (cadastrar outro igual)" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"><Copy className="h-4 w-4" /> <span className="hidden sm:inline">Duplicar</span></button>
          <button data-testid={`admin-edit-${p.id}`} onClick={() => onEdit(p)} className="rounded-full border border-white/15 p-2.5 text-white hover:bg-white/10" aria-label="Editar"><Pencil className="h-4 w-4" /></button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid={`admin-delete-${p.id}`} className="rounded-full border border-white/15 p-2.5 text-red-400 hover:bg-red-500/10" aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark border-white/10 bg-[#111113] text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir {p.model}?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">Essa ação não pode ser desfeita.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-delete-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="admin-delete-confirm" onClick={() => onDelete(p)} className="bg-red-500 text-white hover:bg-red-600">Excluir</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};
